import { remark } from "remark";
import remarkGfm from "remark-gfm";
import { teammateIdSchema } from "../../entities/teammate.js";
import { decodeTranscriptSnippetMarker } from "../../helpers/transcriptSnippetLocation.js";
import { assertUnreachable } from "../../helpers/utilities.js";
import { repairMarkdownTables } from "./markdownTableRepair.js";
import {
  allStructuredContentDateTypes
} from "./types.js";
function extractInteractionIds(content) {
  const ids = [];
  for (const match of content.matchAll(/interaction_id="(?<id>[^"]+)"/g)) {
    const id = match.groups?.id;
    if (id) {
      ids.push(id);
    }
  }
  return ids;
}
function parseStructuredContentFromModelOutput(text, resolveInteractionId) {
  const tree = remark().use(remarkGfm).parse(repairMarkdownTables(text));
  return {
    type: "root",
    children: convertFlowContent(tree.children, resolveInteractionId)
  };
}
function convertFlowContent(nodes, resolve) {
  const result = [];
  for (const node of nodes) {
    switch (node.type) {
      case "heading":
        result.push({
          type: "heading",
          children: [
            { type: "text", value: flattenToPlainText(node.children) }
          ]
        });
        break;
      case "paragraph": {
        const children = convertPhrasingRun(node.children, resolve);
        if (children.length > 0) {
          result.push({ type: "paragraph", children });
        }
        break;
      }
      case "list": {
        const items = collectListItems(node, resolve);
        if (items.length > 0) {
          result.push({ type: "list", children: items });
        }
        break;
      }
      case "table": {
        const table = convertTable(node, resolve);
        if (table) {
          result.push(table);
        }
        break;
      }
      case "blockquote":
        result.push(...convertFlowContent(node.children, resolve));
        break;
      case "code":
        result.push({
          type: "paragraph",
          children: [{ type: "text", value: node.value }]
        });
        break;
      // Everything else (thematicBreak, definition, image, block-level html,
      // footnote definitions, …) has no place in our model and is dropped.
      default:
        break;
    }
  }
  return result;
}
function convertTable(table, resolve) {
  const rows = table.children.map((row) => ({
    type: "tableRow",
    children: row.children.map((cell) => ({
      type: "tableCell",
      children: convertPhrasingRun(cell.children, resolve)
    }))
  }));
  if (rows.length === 0) {
    return void 0;
  }
  const align = table.align ?? void 0;
  return { type: "table", ...align ? { align } : {}, children: rows };
}
function collectListItems(list, resolve) {
  const items = [];
  for (const item of list.children) {
    const inlineRun = [];
    const nestedLists = [];
    for (const block of item.children) {
      if (block.type === "paragraph") {
        inlineRun.push(...convertPhrasingRun(block.children, resolve));
      } else if (block.type === "list") {
        nestedLists.push(block);
      } else if (block.type === "blockquote") {
        for (const inner of block.children) {
          if (inner.type === "paragraph") {
            inlineRun.push(...convertPhrasingRun(inner.children, resolve));
          } else if (inner.type === "list") {
            nestedLists.push(inner);
          }
        }
      }
    }
    if (inlineRun.length > 0) {
      items.push({ type: "listItem", children: inlineRun });
    }
    for (const nested of nestedLists) {
      items.push(...collectListItems(nested, resolve));
    }
  }
  return items;
}
function convertPhrasingRun(nodes, resolve) {
  const result = [];
  let index = 0;
  while (index < nodes.length) {
    const node = nodes[index];
    if (node === void 0) {
      break;
    }
    if (node.type === "html") {
      const openTag = matchOpenTag(node.value);
      if (openTag) {
        const closeIndex = findCloseTagIndex(nodes, index + 1, openTag.name);
        if (closeIndex !== -1) {
          const inner = convertPhrasingRun(
            nodes.slice(index + 1, closeIndex),
            resolve
          );
          result.push(...wrapTag(openTag, inner, resolve));
          index = closeIndex + 1;
          continue;
        }
      }
      index++;
      continue;
    }
    result.push(...convertPhrasingNode(node, resolve));
    index++;
  }
  return result;
}
function convertPhrasingNode(node, resolve) {
  switch (node.type) {
    case "text":
      return [{ type: "text", value: node.value }];
    case "emphasis":
      return [
        {
          type: "emphasis",
          children: convertPhrasingRun(node.children, resolve)
        }
      ];
    case "strong":
      return [
        {
          type: "strong",
          children: convertPhrasingRun(node.children, resolve)
        }
      ];
    case "delete":
      return convertPhrasingRun(node.children, resolve);
    case "inlineCode":
      return [{ type: "text", value: node.value }];
    case "break":
      return [{ type: "text", value: " " }];
    case "link":
    case "linkReference":
      return convertPhrasingRun(node.children, resolve);
    // images, footnotes, and stray html nodes have no place in our model.
    default:
      return [];
  }
}
function matchOpenTag(value) {
  const match = value.trim().match(/^<(?<name>[a-zA-Z]+)(?<attrs>\s[^>]*)?>$/);
  const name = match?.groups?.name;
  if (name !== "red" && name !== "green" && name !== "customer" && name !== "contact" && name !== "person" && name !== "date" && name !== "quote" && name !== "teammate") {
    return void 0;
  }
  const attrs = match?.groups?.attrs ?? "";
  const interactionId = attrs.match(/interaction_id="(?<id>[^"]+)"/)?.groups?.id;
  const rawTeammateId = attrs.match(/\bid="(?<id>[^"]+)"/)?.groups?.id;
  const teammateId = name === "teammate" && rawTeammateId !== void 0 ? teammateIdSchema.safeParse(rawTeammateId).data : void 0;
  const rawDateType = attrs.match(/(?<![\w-])type="(?<type>[^"]+)"/)?.groups?.type;
  const dateType = allStructuredContentDateTypes.find(
    (candidate) => candidate === rawDateType
  );
  const speaker = attrs.match(/speaker="(?<speaker>customer|agent)"/)?.groups?.speaker;
  const sourceAttribute = name === "quote" ? attrs.match(
    /(?<![\w-])source\s*=\s*(?:"(?<double>[^"]*)"|'(?<single>[^']*)'|(?<bare>[^\s>]+))/i
  ) : null;
  const sourceMarker = sourceAttribute?.groups?.double ?? sourceAttribute?.groups?.single ?? sourceAttribute?.groups?.bare;
  return {
    name,
    interactionId,
    teammateId,
    dateType,
    speaker,
    sourceMarker
  };
}
function findCloseTagIndex(nodes, start, name) {
  const closeTag = new RegExp(`^</${name}>$`);
  for (let index = start; index < nodes.length; index++) {
    const node = nodes[index];
    if (node?.type === "html" && closeTag.test(node.value.trim())) {
      return index;
    }
  }
  return -1;
}
function wrapTag(openTag, inner, resolve) {
  switch (openTag.name) {
    case "red":
    case "green":
      return [{ type: "highlight", color: openTag.name, children: inner }];
    case "customer":
    case "contact": {
      const resolved = openTag.interactionId ? resolve(openTag.interactionId) : void 0;
      return [
        {
          type: "grading_reference",
          gradingId: resolved?.gradingId,
          ...resolved?.playbookId ? { playbookId: resolved.playbookId } : {},
          children: inner
        }
      ];
    }
    case "person":
      return [{ type: "person_reference", children: inner }];
    case "date":
      return openTag.dateType ? [
        {
          type: "date_reference",
          dateType: openTag.dateType,
          children: inner
        }
      ] : inner;
    case "quote": {
      const resolved = openTag.interactionId ? resolve(openTag.interactionId) : void 0;
      const markedLocation = resolved && openTag.sourceMarker ? decodeTranscriptSnippetMarker(openTag.sourceMarker) : void 0;
      return [
        {
          type: "quote",
          ...openTag.speaker ? { speaker: openTag.speaker } : {},
          gradingId: resolved?.gradingId,
          ...resolved?.playbookId ? { playbookId: resolved.playbookId } : {},
          ...markedLocation ? { transcriptSnippetLocation: markedLocation } : {},
          children: inner
        }
      ];
    }
    case "teammate": {
      if (!openTag.teammateId) {
        return inner;
      }
      return [
        {
          type: "teammate_reference",
          teammateId: openTag.teammateId,
          children: inner
        }
      ];
    }
    default:
      return assertUnreachable(openTag.name, () => inner);
  }
}
function flattenToPlainText(nodes) {
  return nodes.map(flattenNodeToPlainText).join("").trim();
}
function flattenNodeToPlainText(node) {
  switch (node.type) {
    case "text":
    case "inlineCode":
      return node.value;
    case "emphasis":
    case "strong":
    case "delete":
    case "link":
      return node.children.map(flattenNodeToPlainText).join("");
    case "break":
      return " ";
    default:
      return "";
  }
}
export {
  extractInteractionIds,
  parseStructuredContentFromModelOutput
};
//# sourceMappingURL=parser.js.map
